import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import {
  getGovernanceCurrent,
  type GovernanceCurrent,
} from '../../api/governanceApi';
import { useGovernanceLive } from '../../hooks/useGovernanceLive';
import { ApiError } from '../../api/client';
import './governanceLive.css';
import '../AgentHistory/agentHistory.css';

// 권위: ARCH_DASHBOARD.md v1.8 §4.12.
// - Redis-first, DB-fallback (ADR-DASH-01) — source 뱃지로 구분 표시.
// - /topic/governance STOMP 수신 시 현재 상태 즉시 invalidate.
// - 폴링 fallback 60초.

const POLL_INTERVAL = 60_000;

function formatKst(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function msUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return t - Date.now();
}

function formatCountdown(ms: number | null): string {
  if (ms === null) return '—';
  if (ms <= 0) return '만료됨';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function scoreSignClass(v: number | null | undefined): string {
  if (v == null) return '';
  if (v > 0) return 'gov-score__value--up';
  if (v < 0) return 'gov-score__value--down';
  return '';
}

export default function GovernanceLivePage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [, setNow] = useState(Date.now()); // 카운트다운 tick

  const query = useQuery<GovernanceCurrent, ApiError>({
    queryKey: ['governance', 'current'],
    queryFn: getGovernanceCurrent,
    refetchInterval: POLL_INTERVAL,
    staleTime: 30_000,
    retry: false,
  });

  // STOMP — 새 PUBLISHED / OVERRIDE 발생 시 즉시 재조회.
  const onLive = useCallback(
    (ev: { event_code: string }) => {
      const code = ev.event_code;
      if (
        code === 'GOVERNANCE.CONTEXT.PUBLISHED' ||
        code === 'GOVERNANCE.CONTEXT.PUBLISH_DEGRADED' ||
        code === 'GOVERNANCE.CONTEXT.PUBLISH_FAILED' ||
        code === 'GOVERNANCE.MANUAL.OVERRIDE_SET' ||
        code === 'GOVERNANCE.MANUAL.OVERRIDE_CLEARED' ||
        code === 'GOVERNANCE.AGENT.DOWN'
      ) {
        qc.invalidateQueries({ queryKey: ['governance', 'current'] });
      }
    },
    [qc],
  );
  useGovernanceLive(onLive);

  // 초 단위 tick — valid_until 카운트다운 refresh.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const data = query.data ?? null;
  const validMs = useMemo(() => msUntil(data?.valid_until), [data?.valid_until]);

  return (
    <div className="gov-page">
      <header className="gov-page__header">
        <h1 className="gov-page__title">거버넌스 실시간 현황</h1>
        <div className="gov-page__actions">
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            aria-label="재조회"
          >
            <RefreshCcw
              size={14}
              strokeWidth={1.75}
              style={{ verticalAlign: 'text-bottom', marginRight: 4 }}
            />
            재조회
          </button>
          <button type="button" onClick={() => navigate('/history?tab=6-D')}>
            이력 보기
          </button>
          <Link to="/governance/sla">SLA 감시</Link>
          <Link to="/governance/stage-override">Stage 재정의</Link>
        </div>
      </header>

      {query.isLoading && (
        <div className="gov-muted" style={{ padding: '24px' }}>
          로딩 중…
        </div>
      )}

      {query.isError && (
        <div className="gov-error" role="alert">
          GovernanceContext 로드 실패: {query.error?.message ?? 'unknown'}
          {query.error?.httpStatus === 404 && (
            <div style={{ marginTop: 8 }}>
              Redis/DB 모두에 활성 GovernanceContext 가 없습니다 (AGENT_DOWN 의심).
            </div>
          )}
        </div>
      )}

      {data && (
        <>
          {/* 상단 요약 배너 */}
          <section className="gov-banner">
            <div className="gov-banner__left">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div className="gov-banner__sub">
                  trading_date {data.trading_date}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    className={`gov-hist-stage gov-hist-stage--${String(
                      data.stage,
                    ).toLowerCase()}`}
                  >
                    {data.stage}
                  </span>
                  <span
                    className={`gov-score__value ${scoreSignClass(data.total_score)}`}
                    style={{ fontSize: 'var(--text-xl)' }}
                  >
                    {data.total_score != null ? data.total_score.toFixed(1) : '—'}
                  </span>
                  <span className="gov-banner__sub">
                    max_invest {(data.max_invest_ratio * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
            <div className="gov-banner__right">
              <span
                className={`gov-source-badge gov-source-badge--${data.source}`}
                title="조회 소스 — Redis 우선, 미수신 시 DB 폴백"
              >
                <span>source</span>
                <strong>{data.source}</strong>
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span className="gov-banner__sub">
                  published_at {formatKst(data.published_at)} KST
                </span>
                <span className="gov-countdown">
                  valid_until {formatKst(data.valid_until)} · 남은시간{' '}
                  {formatCountdown(validMs)}
                </span>
              </div>
            </div>
          </section>

          {/* Degraded/Failed 경고 */}
          {data.degraded &&
            ((data.degraded.degraded_7d_count ?? 0) > 0 ||
              data.degraded.last_failed_at) && (
              <div className="gov-warning" role="status">
                <AlertTriangle
                  size={14}
                  strokeWidth={1.75}
                  style={{ verticalAlign: 'text-bottom', marginRight: 6 }}
                />
                최근 7일 PUBLISH_DEGRADED {data.degraded.degraded_7d_count ?? 0}건
                {data.degraded.last_failed_at && (
                  <span style={{ marginLeft: 8 }}>
                    · 마지막 PUBLISH_FAILED {formatKst(data.degraded.last_failed_at)}
                  </span>
                )}
              </div>
            )}

          {/* 섹션 A — 4스코어 */}
          <section className="gov-card">
            <h2 className="gov-card__title">섹션 A · 4 Factor Score</h2>
            <div className="gov-grid-4">
              <ScoreCell label="trend" value={data.scores.trend} />
              <ScoreCell label="spread" value={data.scores.spread} />
              <ScoreCell label="breakout" value={data.scores.breakout} />
              <ScoreCell label="damage" value={data.scores.damage} />
            </div>
          </section>

          {/* 섹션 B — Override 2종 */}
          <section className="gov-card">
            <h2 className="gov-card__title">섹션 B · Override 활성 상태</h2>
            <div className="gov-grid-2">
              <OverrideCard
                title="Rapid Drop"
                flag={data.override.rapid_drop}
                metricLabel="drop_value"
                metricKey="drop_value"
              />
              <OverrideCard
                title="Volatility Expansion"
                flag={data.override.volatility_expansion}
                metricLabel="vix_jump"
                metricKey="vix_jump"
              />
            </div>
          </section>

          {/* 섹션 C — LLM advisory */}
          <section className="gov-card">
            <h2 className="gov-card__title">섹션 C · LLM 보정 (지점 A)</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="gov-banner__sub">
                  applied: <strong>{data.llm_override.applied ? 'YES' : 'NO'}</strong>
                </span>
                {data.llm_override.rollout_mode && (
                  <span className="gov-llm-mode">
                    {data.llm_override.rollout_mode}
                  </span>
                )}
                {data.llm_override.delta_score !== null &&
                  data.llm_override.delta_score !== undefined && (
                    <span className="gov-countdown">
                      Δ {data.llm_override.delta_score > 0 ? '+' : ''}
                      {data.llm_override.delta_score}
                    </span>
                  )}
                {data.llm_override.model && (
                  <span className="gov-banner__sub">model {data.llm_override.model}</span>
                )}
                {data.llm_override.cost_usd !== null &&
                  data.llm_override.cost_usd !== undefined && (
                    <span className="gov-banner__sub">
                      cost ${data.llm_override.cost_usd.toFixed(4)}
                    </span>
                  )}
              </div>
              {data.llm_override.rationale && (
                <>
                  <button
                    type="button"
                    className="gov-btn"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => setExpanded((p) => !p)}
                  >
                    {expanded ? 'rationale 접기' : 'rationale 펼치기'}
                  </button>
                  {expanded && (
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--text-sm)',
                        background: 'var(--parchment)',
                        padding: 12,
                        borderRadius: 4,
                        margin: 0,
                      }}
                    >
                      {data.llm_override.rationale}
                    </pre>
                  )}
                </>
              )}
            </div>
          </section>

          {/* 섹션 D — 배포 헬스 */}
          <section className="gov-card">
            <h2 className="gov-card__title">섹션 D · 배포 헬스</h2>
            <table className="gov-table">
              <tbody>
                <tr>
                  <th style={{ width: '30%' }}>source</th>
                  <td>
                    <span className={`gov-source-badge gov-source-badge--${data.source}`}>
                      <strong>{data.source}</strong>
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Redis TTL</th>
                  <td className="num">
                    {data.redis_ttl_seconds !== null && data.redis_ttl_seconds !== undefined
                      ? `${data.redis_ttl_seconds}s`
                      : '—'}
                  </td>
                </tr>
                <tr>
                  <th>long_allowed / short_allowed</th>
                  <td className="num">
                    {data.long_allowed ? '✓' : '✗'} / {data.short_allowed ? '✓' : '✗'}
                  </td>
                </tr>
                <tr>
                  <th>마지막 PUBLISHED event_id</th>
                  <td>
                    {data.last_event_id ? (
                      <Link to={`/history/${data.last_event_id}`}>{data.last_event_id}</Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                {data.file_path && (
                  <tr>
                    <th>파일 경로</th>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{data.file_path}</td>
                  </tr>
                )}
                {data.file_mtime && (
                  <tr>
                    <th>파일 mtime</th>
                    <td>{formatKst(data.file_mtime)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

function ScoreCell({
  label,
  value,
}: {
  label: string;
  value: number | null;
}): JSX.Element {
  let display: string;
  if (value === null || value === undefined) {
    display = '—';
  } else if (value === 0) {
    display = '0.0';
  } else {
    display = `${value > 0 ? '+' : ''}${value.toFixed(1)}`;
  }
  return (
    <div className="gov-score">
      <span className="gov-score__label">{label}</span>
      <span className={`gov-score__value ${scoreSignClass(value)}`}>{display}</span>
    </div>
  );
}

function OverrideCard({
  title,
  flag,
  metricLabel,
  metricKey,
}: {
  title: string;
  flag: {
    active: boolean;
    triggered_at?: string | null;
    drop_value?: number | null;
    vix_jump?: number | null;
    rationale?: string | null;
  };
  metricLabel: string;
  metricKey: 'drop_value' | 'vix_jump';
}): JSX.Element {
  return (
    <div className={`gov-override-card${flag.active ? ' gov-override-card--active' : ''}`}>
      <div className="gov-override-card__title">
        {flag.active && (
          <AlertTriangle size={14} strokeWidth={1.75} color="var(--danger)" />
        )}
        {title} {flag.active ? '— 발동 중' : '— 정상'}
      </div>
      {flag.active && (
        <>
          <div className="gov-override-card__meta">
            triggered_at {flag.triggered_at ? new Date(flag.triggered_at).toLocaleString('ko-KR') : '—'}
          </div>
          <div className="gov-override-card__meta">
            {metricLabel}: {flag[metricKey] ?? '—'}
          </div>
          {flag.rationale && (
            <div className="gov-override-card__rationale">{flag.rationale}</div>
          )}
        </>
      )}
    </div>
  );
}
